import React, { useState, useEffect } from 'react';
import {
    Layout,
    Card,
    Button,
    Space,
    Typography,
    Tag,
    Modal,
    Form,
    message,
    Menu,
    Row,
    Col,
    Input,
    Select,
    DatePicker,
    Switch,
    Popconfirm,
    Tabs
} from 'antd';
import {
    UserOutlined,
    HomeOutlined,
    BookOutlined,
    CarryOutOutlined,
    PhoneOutlined,
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    BankOutlined,
    GithubOutlined,
    IdcardOutlined,
    SafetyOutlined,
    DeleteOutlined,
    PlusOutlined,
    CalendarOutlined,
    DollarOutlined,
    FileTextOutlined,
    LockOutlined,
    UnlockOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import * as adminactions from "store/actions/AuthActions";
import SetPassword from "../Auth/SetPassword";
import EmployeeProfile from "./EmployeeProfile";
import SalaryInfoTab from "../../pages/Employees/SalaryInfoTab";
import EmployeeTimesheet from "./EmployeeTimesheet";
import EmployeePayslips from "./EmployeePayslips";
import EmployeeDailyUpdates from "./EmployeeDailyUpdates";
import EmployeeAttendance from "./EmployeeAttendance";
import useUrlFilters from 'hooks/useUrlFilters';

const { Title, Text } = Typography;
const { Sider, Content } = Layout;
const { Option } = Select;
const { TextArea } = Input;

// Active tab lives in the URL so links to a tab's filtered view open on that tab.
const URL_FILTERS = {
    tab: { type: 'string', default: '1' },
};

const EmployeeDetail = () => {
    const { id } = useParams();
    const [form] = Form.useForm();

    // State management
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS);
    const activeTab = urlFilters.tab;
    const setActiveTab = (value) => setUrlFilters({ tab: value }, { replace: false });
    const [activeSection, setActiveSection] = useState('basic');

    // Section-specific edit states
    const [isEditingBasic, setIsEditingBasic] = useState(false);
    const [isEditingEmployment, setIsEditingEmployment] = useState(false);
    const [isEditingFinancial, setIsEditingFinancial] = useState(false);
    const [isEditingSecurity, setIsEditingSecurity] = useState(false);
    const [isEditingSocial, setIsEditingSocial] = useState(false);

    // Related data
    const [addresses, setAddresses] = useState([]);
    const [education, setEducation] = useState([]);
    const [experience, setExperience] = useState([]);
    const [emergencyContacts, setEmergencyContacts] = useState([]);
    const [users, setUsers] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]);

    // Password management
    const [showSetPassword, setShowSetPassword] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Modal for adding/editing records
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState('');
    const [editingRecord, setEditingRecord] = useState(null);
    const [modalForm] = Form.useForm();

    useEffect(() => {
        if (id) {
            loadEmployeeData();
        }
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadEmployeeData = async () => {
        setLoading(true);
        try {
            const [employeeResponse, usersResponse, orgsResponse, employeesResponse] = await Promise.all([
                adminactions.retrieveEmployee(id),
                adminactions.loadUsers(1, ''),
                adminactions.loadOrganizations(),
                adminactions.loadEmployees()
            ]);

            if (employeeResponse && !employeeResponse.message) {
                setEmployee(employeeResponse);

                // Load related data
                const [addressesRes, educationRes, experienceRes, emergencyRes] = await Promise.all([
                    adminactions.loadEmployeeAddresses(id),
                    adminactions.loadEmployeeEducation(id),
                    adminactions.loadEmployeeExperience(id),
                    adminactions.loadEmployeeEmergencyContacts(id)
                ]);

                setAddresses(Array.isArray(addressesRes) ? addressesRes : []);
                setEducation(Array.isArray(educationRes) ? educationRes : []);
                setExperience(Array.isArray(experienceRes) ? experienceRes : []);
                setEmergencyContacts(Array.isArray(emergencyRes) ? emergencyRes : []);

                // Set form values for edit mode
                const formData = {
                    ...employeeResponse,
                    date_of_joining: employeeResponse.date_of_joining ? dayjs(employeeResponse.date_of_joining) : null,
                    last_working_day: employeeResponse.last_working_day ? dayjs(employeeResponse.last_working_day) : null,
                    date_of_birth: employeeResponse.date_of_birth ? dayjs(employeeResponse.date_of_birth) : null,
                    user_id: employeeResponse.user?.id,
                    organization_id: employeeResponse.organization?.id,
                    is_2fa_enabled: employeeResponse.user?.is_2fa_enabled || false,
                };
                form.setFieldsValue(formData);
            }

            if (usersResponse?.results) {
                setUsers(usersResponse.results);
            }
            if (orgsResponse?.results) {
                setOrganizations(orgsResponse.results);
            } else if (Array.isArray(orgsResponse)) {
                setOrganizations(orgsResponse);
            }
            if (employeesResponse?.results) {
                setAllEmployees(employeesResponse.results);
            } else if (Array.isArray(employeesResponse)) {
                setAllEmployees(employeesResponse);
            }
        } catch (error) {
            console.error('Error loading employee data:', error);
            message.error('Failed to load employee data');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveSection = async (section) => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            // Merge with original employee data to ensure all required fields are present
            const formattedValues = {
                employee_id: employee.employee_id,
                user_id: employee.user?.id,
                organization_id: employee.organization?.id,
                job_title: employee.job_title,
                department: employee.department,
                employment_type: employee.employment_type,
                ...employee, // Include all original employee fields
                ...values, // Override with form values
                date_of_joining: values.date_of_joining ? values.date_of_joining.format('YYYY-MM-DD') : employee.date_of_joining,
                last_working_day: values.last_working_day ? values.last_working_day.format('YYYY-MM-DD') : employee.last_working_day,
                date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : employee.date_of_birth,
            };

            // Handle user-specific fields
            const userData = {};
            if (values.is_2fa_enabled !== undefined) {
                userData.is_2fa_enabled = values.is_2fa_enabled;
            }
            if (Object.keys(userData).length > 0) {
                formattedValues.user_data = userData;
            }
            delete formattedValues.is_2fa_enabled;

            const response = await adminactions.updateEmployees(formattedValues, id);

            if (response && !response.message) {
                message.success(`${section.charAt(0).toUpperCase() + section.slice(1)} updated successfully!`);

                // Close the specific section
                switch(section) {
                    case 'basic':
                        setIsEditingBasic(false);
                        break;
                    case 'employment':
                        setIsEditingEmployment(false);
                        break;
                    case 'financial':
                        setIsEditingFinancial(false);
                        break;
                    case 'security':
                        setIsEditingSecurity(false);
                        break;
                    case 'social':
                        setIsEditingSocial(false);
                        break;
                    default:
                        break;
                }

                loadEmployeeData(); // Reload data
            } else {
                message.error(response?.message || 'Failed to update employee');
            }
        } catch (error) {
            console.error('Error updating employee:', error);
            message.error('Failed to update employee');
        } finally {
            setSaving(false);
        }
    };

    const handleCancelSection = (section) => {
        // Reset form to original values
        const formData = {
            ...employee,
            date_of_joining: employee.date_of_joining ? dayjs(employee.date_of_joining) : null,
            last_working_day: employee.last_working_day ? dayjs(employee.last_working_day) : null,
            date_of_birth: employee.date_of_birth ? dayjs(employee.date_of_birth) : null,
            user_id: employee.user?.id,
            organization_id: employee.organization?.id,
            is_2fa_enabled: employee.user?.is_2fa_enabled || false,
        };
        form.setFieldsValue(formData);

        // Close the specific section
        switch(section) {
            case 'basic':
                setIsEditingBasic(false);
                break;
            case 'employment':
                setIsEditingEmployment(false);
                break;
            case 'financial':
                setIsEditingFinancial(false);
                break;
            case 'security':
                setIsEditingSecurity(false);
                break;
            case 'social':
                setIsEditingSocial(false);
                break;
            default:
                break;
        }
    };

    const handleSetPassword = () => {
        if (employee?.user) {
            setSelectedUser(employee.user);
            setShowSetPassword(true);
        }
    };

    const handleProfileLockToggle = async (checked) => {
        try {
            const response = await adminactions.toggleEmployeeProfileLock(id, checked);
            if (response && !response.error) {
                setEmployee(prev => ({ ...prev, profile_locked: checked }));
                message.success(`Profile ${checked ? 'locked' : 'unlocked'} successfully`);
            } else {
                message.error('Failed to update profile lock status');
            }
        } catch (error) {
            console.error('Error toggling profile lock:', error);
            message.error('Failed to update profile lock status');
        }
    };

    const showModal = (type, record = null) => {
        setModalType(type);
        setEditingRecord(record);
        setModalVisible(true);
        if (record) {
            if (type === 'education' || type === 'experience') {
                modalForm.setFieldsValue({
                    ...record,
                    start_date: record.start_date ? dayjs(record.start_date) : null,
                    end_date: record.end_date ? dayjs(record.end_date) : null,
                });
            } else {
                modalForm.setFieldsValue(record);
            }
        } else {
            modalForm.resetFields();
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setModalType('');
        setEditingRecord(null);
        modalForm.resetFields();
    };

    const handleDelete = async (type, recordId) => {
        try {
            let response;
            switch (type) {
                case 'address':
                    response = await adminactions.deleteEmployeeAddress(recordId);
                    break;
                case 'education':
                    response = await adminactions.deleteEmployeeEducation(recordId);
                    break;
                case 'experience':
                    response = await adminactions.deleteEmployeeExperience(recordId);
                    break;
                case 'emergency':
                    response = await adminactions.deleteEmployeeEmergencyContact(recordId);
                    break;
                default:
                    break;
            }

            if (response) {
                message.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
                loadEmployeeData(); // Reload all data
            }
        } catch (error) {
            message.error(`Failed to delete ${type}`);
            console.error('Delete error:', error);
        }
    };

    const handleModalSubmit = async (values) => {
        try {
            let response;
            const data = { ...values, employee: id };

            // Handle date formatting for education and experience
            if (modalType === 'education' || modalType === 'experience') {
                data.start_date = values.start_date ? values.start_date.format('YYYY-MM-DD') : null;
                data.end_date = values.end_date ? values.end_date.format('YYYY-MM-DD') : null;
            }

            switch (modalType) {
                case 'address':
                    if (editingRecord) {
                        response = await adminactions.updateEmployeeAddress(editingRecord.id, data);
                    } else {
                        response = await adminactions.addEmployeeAddress(data);
                    }
                    break;
                case 'education':
                    if (editingRecord) {
                        response = await adminactions.updateEmployeeEducation(editingRecord.id, data);
                    } else {
                        response = await adminactions.addEmployeeEducation(data);
                    }
                    break;
                case 'experience':
                    if (editingRecord) {
                        response = await adminactions.updateEmployeeExperience(editingRecord.id, data);
                    } else {
                        response = await adminactions.addEmployeeExperience(data);
                    }
                    break;
                case 'emergency':
                    if (editingRecord) {
                        response = await adminactions.updateEmployeeEmergencyContact(data, editingRecord.id);
                    } else {
                        response = await adminactions.addEmployeeEmergencyContact(data);
                    }
                    break;
                default:
                    break;
            }

            if (response && !response.message) {
                message.success(`${modalType.charAt(0).toUpperCase() + modalType.slice(1)} ${editingRecord ? 'updated' : 'added'} successfully!`);
                handleModalCancel();
                loadEmployeeData(); // Reload all data
            } else {
                message.error(response?.message || `Failed to save ${modalType}`);
            }
        } catch (error) {
            message.error(`Failed to save ${modalType}`);
            console.error('Save error:', error);
        }
    };

    const menuItems = [
        { key: 'basic', icon: <IdcardOutlined />, label: 'Basic Information' },
        { key: 'employment', icon: <CarryOutOutlined />, label: 'Employment Details' },
        { key: 'financial', icon: <BankOutlined />, label: 'Financial Information' },
        { key: 'security', icon: <SafetyOutlined />, label: 'Security Settings' },
        { key: 'addresses', icon: <HomeOutlined />, label: `Addresses (${addresses.length})` },
        { key: 'qualification', icon: <BookOutlined />, label: `Qualification (${education.length})` },
        { key: 'experience', icon: <CarryOutOutlined />, label: `Work Experience (${experience.length})` },
        { key: 'social', icon: <GithubOutlined />, label: 'Developer Profiles' },
        { key: 'emergency', icon: <PhoneOutlined />, label: `Emergency Contacts (${emergencyContacts.length})` },
    ];

    const renderFieldRow = (label, value, span = 8) => (
        <Col xs={24} sm={12} md={span} key={label}>
            <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>{label}</Text>
                <Text strong>{value || '-'}</Text>
            </div>
        </Col>
    );

    const renderEditField = (name, label, type = 'input', options = {}, span = 8) => (
        <Col xs={24} sm={12} md={span} key={name}>
            <Form.Item
                label={label}
                name={name}
                rules={options.required ? [{ required: true, message: `Please enter ${label.toLowerCase()}!` }] : []}
            >
                {type === 'input' && <Input {...options} />}
                {type === 'select' && <Select {...options}>{options.children}</Select>}
                {type === 'date' && <DatePicker style={{ width: '100%' }} {...options} />}
                {type === 'switch' && <Switch {...options} />}
                {type === 'textarea' && <TextArea {...options} />}
            </Form.Item>
        </Col>
    );

    const renderBasicInformation = () => {
        return (
            <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    {!isEditingBasic ? (
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => setIsEditingBasic(true)}
                        >
                            Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => handleCancelSection('basic')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={() => handleSaveSection('basic')}
                                loading={saving}
                            >
                                Save
                            </Button>
                        </Space>
                    )}
                </div>

                {isEditingBasic ? (
                    <Row gutter={[24, 0]}>
                        {renderEditField('employee_id', 'Employee ID', 'input', { required: true })}
                        {renderEditField('user_id', 'User Account', 'select', {
                            required: true,
                            showSearch: true,
                            children: users.map(user => (
                                <Option key={user.id} value={user.id}>
                                    {user.first_name} {user.last_name} ({user.email})
                                </Option>
                            ))
                        })}
                        {renderEditField('organization_id', 'Organization', 'select', {
                            required: true,
                            showSearch: true,
                            children: organizations.map(org => (
                                <Option key={org.id} value={org.id}>
                                    {org.name}
                                </Option>
                            ))
                        })}
                        {renderEditField('date_of_birth', 'Date of Birth', 'date')}
                    </Row>
                ) : (
                    <Row gutter={[24, 16]}>
                        {renderFieldRow('Employee ID', employee?.employee_id)}
                        {renderFieldRow('User Account', `${employee?.user?.first_name || ''} ${employee?.user?.last_name || ''} (${employee?.user?.email || ''})`)}
                        {renderFieldRow('Organization', employee?.organization?.name)}
                        {renderFieldRow('Date of Birth', employee?.date_of_birth ? dayjs(employee.date_of_birth).format('YYYY-MM-DD') : null)}
                    </Row>
                )}
            </>
        );
    };

    const renderEmploymentDetails = () => {
        return (
            <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    {!isEditingEmployment ? (
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => setIsEditingEmployment(true)}
                        >
                            Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => handleCancelSection('employment')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={() => handleSaveSection('employment')}
                                loading={saving}
                            >
                                Save
                            </Button>
                        </Space>
                    )}
                </div>

                {isEditingEmployment ? (
                    <Row gutter={[24, 0]}>
                        {renderEditField('job_title', 'Job Title', 'input', { required: true })}
                        {renderEditField('department', 'Department', 'input', { required: true })}
                        {renderEditField('employment_type', 'Employment Type', 'select', {
                            required: true,
                            children: (
                                <>
                                    <Option value="Full-time">Full-time</Option>
                                    <Option value="Part-time">Part-time</Option>
                                    <Option value="Contract">Contract</Option>
                                </>
                            )
                        })}
                        {renderEditField('date_of_joining', 'Date of Joining', 'date')}
                        {renderEditField('last_working_day', 'Last Working Day', 'date')}
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item label="Report To" name="report_to">
                                <Select
                                    allowClear
                                    showSearch
                                    placeholder="Select employee"
                                    optionFilterProp="label"
                                >
                                    {allEmployees
                                        .filter(emp => emp.id !== id)
                                        .map(emp => (
                                            <Option key={emp.id} value={emp.id} label={emp.user_name || emp.user_email}>
                                                {emp.user_name || emp.user_email} {emp.employee_id && `(${emp.employee_id})`}
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </Form.Item>
                        </Col>
                        {renderEditField('machine_id', 'Machine ID', 'input')}
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Consider Attendance for Payroll"
                                name="is_attendance_considered"
                                valuePropName="checked"
                                tooltip="When enabled, attendance will be factored into payroll calculations."
                            >
                                <Switch
                                    checkedChildren="Yes"
                                    unCheckedChildren="No"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                ) : (
                    <Row gutter={[24, 16]}>
                        {renderFieldRow('Job Title', employee?.job_title)}
                        {renderFieldRow('Department', employee?.department)}
                        {renderFieldRow('Employment Type', employee?.employment_type)}
                        {renderFieldRow('Date of Joining', employee?.date_of_joining ? dayjs(employee.date_of_joining).format('YYYY-MM-DD') : null)}
                        {renderFieldRow('Last Working Day', employee?.last_working_day ? dayjs(employee.last_working_day).format('YYYY-MM-DD') : null)}
                        {renderFieldRow('Report To', employee?.report_to_name)}
                        {renderFieldRow('Machine ID', employee?.machine_id)}
                        {renderFieldRow('Consider Attendance for Payroll', employee?.is_attendance_considered ? 'Yes' : 'No')}
                    </Row>
                )}
            </>
        );
    };

    const renderEmergencyContacts = () => {
        return (
            <>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => showModal('emergency')}
                    >
                        Add Emergency Contact
                    </Button>
                </div>
                {emergencyContacts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        No emergency contacts added yet
                    </div>
                ) : (
                    emergencyContacts.map((contact, index) => (
                        <Card
                            key={contact.id}
                            style={{ marginBottom: 16 }}
                            title={`${contact.name}${contact.is_primary ? ' (Primary)' : ''}`}
                            extra={
                                <Space>
                                    <Button
                                        icon={<EditOutlined />}
                                        size="small"
                                        onClick={() => showModal('emergency', contact)}
                                    />
                                    <Popconfirm
                                        title="Sure to delete?"
                                        onConfirm={() => handleDelete('emergency', contact.id)}
                                    >
                                        <Button
                                            icon={<DeleteOutlined />}
                                            size="small"
                                            danger
                                        />
                                    </Popconfirm>
                                </Space>
                            }
                        >
                            <Row gutter={[24, 16]}>
                                {renderFieldRow('Relationship', contact.relationship)}
                                {renderFieldRow('Phone Number', contact.phone_number)}
                                {renderFieldRow('Alternate Phone', contact.alternate_phone)}
                                {renderFieldRow('Email', contact.email)}
                                {renderFieldRow('Address', contact.address, 24)}
                            </Row>
                        </Card>
                    ))
                )}
            </>
        );
    };

    const renderSocialProfile = () => {
        return (
            <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    {!isEditingSocial ? (
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => setIsEditingSocial(true)}
                        >
                            Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => handleCancelSection('social')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={() => handleSaveSection('social')}
                                loading={saving}
                            >
                                Save
                            </Button>
                        </Space>
                    )}
                </div>

                {isEditingSocial ? (
                    <Row gutter={[24, 0]}>
                        {renderEditField('github_username', 'GitHub Username', 'input')}
                        {renderEditField('gitlab_username', 'GitLab Username', 'input')}
                    </Row>
                ) : (
                    <Row gutter={[24, 16]}>
                        {renderFieldRow('GitHub Username', employee?.github_username)}
                        {renderFieldRow('GitLab Username', employee?.gitlab_username)}
                    </Row>
                )}
            </>
        );
    };

    const renderQualification = () => {
        return (
            <>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => showModal('education')}
                    >
                        Add Education
                    </Button>
                </div>
                {education.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        No education records added yet
                    </div>
                ) : (
                    education.map((edu, index) => (
                        <Card
                            key={edu.id}
                            style={{ marginBottom: 16 }}
                            title={`${edu.degree} - ${edu.institution_name}`}
                            extra={
                                <Space>
                                    <Button
                                        icon={<EditOutlined />}
                                        size="small"
                                        onClick={() => showModal('education', edu)}
                                    />
                                    <Popconfirm
                                        title="Sure to delete?"
                                        onConfirm={() => handleDelete('education', edu.id)}
                                    >
                                        <Button
                                            icon={<DeleteOutlined />}
                                            size="small"
                                            danger
                                        />
                                    </Popconfirm>
                                </Space>
                            }
                        >
                            <Row gutter={[24, 16]}>
                                {renderFieldRow('Field of Study', edu.field_of_study)}
                                {renderFieldRow('Grade', edu.grade)}
                                {renderFieldRow('Start Date', edu.start_date ? dayjs(edu.start_date).format('YYYY-MM-DD') : '-')}
                                {renderFieldRow('End Date', edu.end_date ? dayjs(edu.end_date).format('YYYY-MM-DD') : 'Present')}
                            </Row>
                        </Card>
                    ))
                )}
            </>
        );
    };

    const renderWorkExperience = () => {
        return (
            <>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => showModal('experience')}
                    >
                        Add Experience
                    </Button>
                </div>
                {experience.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        No work experience added yet
                    </div>
                ) : (
                    experience.map((exp, index) => (
                        <Card
                            key={exp.id}
                            style={{ marginBottom: 16 }}
                            title={`${exp.job_title} at ${exp.company_name}`}
                            extra={
                                <Space>
                                    <Button
                                        icon={<EditOutlined />}
                                        size="small"
                                        onClick={() => showModal('experience', exp)}
                                    />
                                    <Popconfirm
                                        title="Sure to delete?"
                                        onConfirm={() => handleDelete('experience', exp.id)}
                                    >
                                        <Button
                                            icon={<DeleteOutlined />}
                                            size="small"
                                            danger
                                        />
                                    </Popconfirm>
                                </Space>
                            }
                        >
                            <Row gutter={[24, 16]}>
                                {renderFieldRow('Start Date', exp.start_date ? dayjs(exp.start_date).format('YYYY-MM-DD') : '-')}
                                {renderFieldRow('End Date', exp.end_date ? dayjs(exp.end_date).format('YYYY-MM-DD') : 'Present')}
                                {renderFieldRow('Responsibilities', exp.responsibilities, 24)}
                            </Row>
                        </Card>
                    ))
                )}
            </>
        );
    };

    const renderFinancialInformation = () => {
        return (
            <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    {!isEditingFinancial ? (
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => setIsEditingFinancial(true)}
                        >
                            Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => handleCancelSection('financial')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={() => handleSaveSection('financial')}
                                loading={saving}
                            >
                                Save
                            </Button>
                        </Space>
                    )}
                </div>

                {isEditingFinancial ? (
                    <Row gutter={[24, 0]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="PAN Card"
                                name="pan_card"
                                rules={[
                                    { pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: 'Invalid PAN format!' }
                                ]}
                            >
                                <Input
                                    placeholder="Enter PAN card number"
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </Form.Item>
                        </Col>
                        {renderEditField('bank_account_number', 'Bank Account Number', 'input')}
                        {renderEditField('bank_name', 'Bank Name', 'input')}
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Bank IFSC Code"
                                name="bank_ifsc"
                            >
                                <Input
                                    placeholder="Enter IFSC code"
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </Form.Item>
                        </Col>
                        {renderEditField('bank_branch', 'Bank Branch', 'input')}
                    </Row>
                ) : (
                    <Row gutter={[24, 16]}>
                        {renderFieldRow('PAN Card', employee?.pan_card)}
                        {renderFieldRow('Bank Account Number', employee?.bank_account_number)}
                        {renderFieldRow('Bank Name', employee?.bank_name)}
                        {renderFieldRow('Bank IFSC Code', employee?.bank_ifsc)}
                        {renderFieldRow('Bank Branch', employee?.bank_branch)}
                    </Row>
                )}
            </>
        );
    };

    const renderSecuritySettings = () => {
        return (
            <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    {!isEditingSecurity ? (
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => setIsEditingSecurity(true)}
                        >
                            Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => handleCancelSection('security')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={() => handleSaveSection('security')}
                                loading={saving}
                            >
                                Save
                            </Button>
                        </Space>
                    )}
                </div>

                {isEditingSecurity ? (
                    <Row gutter={[24, 0]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Two-Factor Authentication"
                                name="is_2fa_enabled"
                                valuePropName="checked"
                            >
                                <Switch
                                    checkedChildren="Enabled"
                                    unCheckedChildren="Disabled"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item label="Password Management">
                                <Button
                                    icon={<SafetyOutlined />}
                                    onClick={handleSetPassword}
                                >
                                    Set Password
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                ) : (
                    <Row gutter={[24, 16]}>
                        {renderFieldRow('Two-Factor Authentication', employee?.user?.is_2fa_enabled ? 'Enabled' : 'Disabled')}
                        <Col xs={24} sm={12} md={8}>
                            <div style={{ marginBottom: 16 }}>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Password Management</Text>
                                <Button
                                    icon={<SafetyOutlined />}
                                    onClick={handleSetPassword}
                                >
                                    Set Password
                                </Button>
                            </div>
                        </Col>
                    </Row>
                )}
            </>
        );
    };

    const renderAddresses = () => {
        return (
            <>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => showModal('address')}
                    >
                        Add Address
                    </Button>
                </div>
                {addresses.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        No addresses added yet
                    </div>
                ) : (
                    addresses.map((address, index) => (
                        <Card
                            key={address.id}
                            style={{ marginBottom: 16 }}
                            title={`Address ${index + 1}`}
                            extra={
                                <Space>
                                    <Button
                                        icon={<EditOutlined />}
                                        size="small"
                                        onClick={() => showModal('address', address)}
                                    />
                                    <Popconfirm
                                        title="Sure to delete?"
                                        onConfirm={() => handleDelete('address', address.id)}
                                    >
                                        <Button
                                            icon={<DeleteOutlined />}
                                            size="small"
                                            danger
                                        />
                                    </Popconfirm>
                                </Space>
                            }
                        >
                            <Row gutter={[24, 16]}>
                                {renderFieldRow('Permanent Address', address.address_line_1)}
                                {renderFieldRow('Current Address', address.address_line_2)}
                                {renderFieldRow('City', address.city)}
                                {renderFieldRow('State', address.state)}
                                {renderFieldRow('Postal Code', address.postal_code)}
                                {renderFieldRow('Country', address.country)}
                                <Col xs={24} sm={12}>
                                    <div>
                                        <span style={{ color: '#666', fontSize: 12 }}>Status</span>
                                        <div style={{ marginTop: 4 }}>
                                            {address.is_active ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    ))
                )}
            </>
        );
    };

    const getSectionTitle = () => {
        const titles = {
            'basic': 'Basic Information',
            'employment': 'Employment Details',
            'financial': 'Financial Information',
            'security': 'Security Settings',
            'addresses': 'Addresses',
            'qualification': 'Qualification',
            'experience': 'Work Experience',
            'social': 'Developer Profiles',
            'emergency': 'Emergency Contacts'
        };
        return titles[activeSection] || 'Information';
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'basic':
                return renderBasicInformation();
            case 'employment':
                return renderEmploymentDetails();
            case 'financial':
                return renderFinancialInformation();
            case 'security':
                return renderSecuritySettings();
            case 'addresses':
                return renderAddresses();
            case 'qualification':
                return renderQualification();
            case 'experience':
                return renderWorkExperience();
            case 'social':
                return renderSocialProfile();
            case 'emergency':
                return renderEmergencyContacts();
            default:
                return <Text>Section not found</Text>;
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 200px)' }}>
                <div>Loading...</div>
            </div>
        );
    }

    if (!employee) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 200px)' }}>
                <div>Employee not found</div>
            </div>
        );
    }

    const renderGeneralTab = () => (
        <Layout style={{ height: '100%', background: '#fff', overflow: 'hidden' }}>
            {/* Sidebar */}
            <Sider
                width={220}
                style={{
                    background: '#fff',
                    borderRight: '1px solid #f0f0f0',
                    overflow: 'auto'
                }}
            >
                <style>
                    {`
                        .employee-sidebar-menu .ant-menu-item-selected {
                            background-color: #1890ff !important;
                            color: #fff !important;
                        }
                        .employee-sidebar-menu .ant-menu-item-selected .anticon {
                            color: #fff !important;
                        }
                        .employee-sidebar-menu .ant-menu-item-selected::after {
                            border-right-color: #1890ff !important;
                        }
                    `}
                </style>
                <Menu
                    mode="inline"
                    selectedKeys={[activeSection]}
                    onClick={({ key }) => setActiveSection(key)}
                    style={{ borderRight: 0 }}
                    items={menuItems}
                    className="employee-sidebar-menu"
                />
            </Sider>

            {/* Content Area */}
            <Content style={{ padding: '24px', overflow: 'auto', background: '#fff' }}>
                <Title level={5} style={{ marginBottom: 24 }}>
                    {getSectionTitle()}
                </Title>
                <Form form={form} layout="vertical">
                    {renderContent()}
                </Form>
            </Content>
        </Layout>
    );

    const tabItems = [
        {
            key: '1',
            label: (
                <span>
                    <IdcardOutlined style={{ marginRight: '8px' }} />
                    General
                </span>
            ),
            children: renderGeneralTab(),
        },
        {
            key: '2',
            label: (
                <span>
                    <UserOutlined style={{ marginRight: '8px' }} />
                    Profile
                </span>
            ),
            children: <EmployeeProfile employeeId={employee?.id} />,
        },
        {
            key: '3',
            label: (
                <span>
                    <DollarOutlined style={{ marginRight: '8px' }} />
                    Salary Info
                </span>
            ),
            children: <SalaryInfoTab employeeId={employee?.id} />,
        },
        {
            key: '4',
            label: (
                <span>
                    <CalendarOutlined style={{ marginRight: '8px' }} />
                    Timesheet
                </span>
            ),
            children: <EmployeeTimesheet employeeId={employee?.id} />,
        },
        {
            key: '6',
            label: (
                <span>
                    <FileTextOutlined style={{ marginRight: '8px' }} />
                    Payslips
                </span>
            ),
            children: <EmployeePayslips employeeId={employee?.id} />,
        },
        {
            key: '7',
            label: (
                <span>
                    <EditOutlined style={{ marginRight: '8px' }} />
                    Daily Updates
                </span>
            ),
            children: <EmployeeDailyUpdates employeeId={employee?.id} />,
        },
        {
            key: '8',
            label: (
                <span>
                    <ClockCircleOutlined style={{ marginRight: '8px' }} />
                    Attendance
                </span>
            ),
            children: <EmployeeAttendance employeeId={employee?.id} />,
        },
    ];

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', padding: '20px' }}>
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Title level={4} style={{ margin: 0 }}>
                            {employee?.user?.first_name} {employee?.user?.last_name}
                        </Title>
                        <Space>
                            {employee?.profile_locked ? <LockOutlined style={{ color: '#f5222d' }} /> : <UnlockOutlined style={{ color: '#52c41a' }} />}
                            <Switch
                                checked={employee?.profile_locked}
                                onChange={handleProfileLockToggle}
                                checkedChildren="Locked"
                                unCheckedChildren="Unlocked"
                                style={{ backgroundColor: employee?.profile_locked ? '#f5222d' : '#52c41a' }}
                            />
                        </Space>
                    </div>
                }
                style={{
                    width: '100%',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
                bodyStyle={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: 0
                }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                    type="line"
                    size="large"
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                    tabBarStyle={{
                        margin: 0,
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        flexShrink: 0
                    }}
                />
            </Card>

            {/* Modal for adding/editing records */}
            <Modal
                title={`${editingRecord ? 'Edit' : 'Add'} ${modalType.charAt(0).toUpperCase() + modalType.slice(1)}`}
                open={modalVisible}
                onCancel={handleModalCancel}
                footer={null}
                width={800}
            >
                <Form
                    form={modalForm}
                    layout="vertical"
                    onFinish={handleModalSubmit}
                >
                    {modalType === 'address' && (
                        <>
                            <Row gutter={16}>
                                <Col xs={24}>
                                    <Form.Item
                                        label="Permanent Address"
                                        name="address_line_1"
                                        rules={[{ required: true, message: 'Please enter permanent address!' }]}
                                    >
                                        <Input placeholder="Enter permanent address" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item
                                        label="Current Address"
                                        name="address_line_2"
                                    >
                                        <Input placeholder="Enter current address" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Form.Item
                                        label="City"
                                        name="city"
                                        rules={[{ required: true, message: 'Please enter city!' }]}
                                    >
                                        <Input placeholder="Enter city" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Form.Item
                                        label="State"
                                        name="state"
                                        rules={[{ required: true, message: 'Please enter state!' }]}
                                    >
                                        <Input placeholder="Enter state" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Form.Item
                                        label="Postal Code"
                                        name="postal_code"
                                        rules={[{ required: true, message: 'Please enter postal code!' }]}
                                    >
                                        <Input placeholder="Enter postal code" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Country"
                                        name="country"
                                        rules={[{ required: true, message: 'Please enter country!' }]}
                                    >
                                        <Input placeholder="Enter country" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Status"
                                        name="is_active"
                                        valuePropName="checked"
                                        initialValue={false}
                                    >
                                        <Switch
                                            checkedChildren="Active"
                                            unCheckedChildren="Inactive"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}

                    {modalType === 'education' && (
                        <>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Institution Name"
                                        name="institution_name"
                                        rules={[{ required: true, message: 'Please enter institution name!' }]}
                                    >
                                        <Input placeholder="Enter institution name" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Degree"
                                        name="degree"
                                        rules={[{ required: true, message: 'Please enter degree!' }]}
                                    >
                                        <Input placeholder="Enter degree" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Field of Study"
                                        name="field_of_study"
                                        rules={[{ required: true, message: 'Please enter field of study!' }]}
                                    >
                                        <Input placeholder="Enter field of study" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Grade"
                                        name="grade"
                                    >
                                        <Input placeholder="Enter grade" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Start Date"
                                        name="start_date"
                                        rules={[{ required: true, message: 'Please select start date!' }]}
                                    >
                                        <DatePicker style={{ width: '100%' }} placeholder="Select start date" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="End Date"
                                        name="end_date"
                                        dependencies={['start_date']}
                                        rules={[
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    const startDate = getFieldValue('start_date');
                                                    if (!value || !startDate) {
                                                        return Promise.resolve();
                                                    }
                                                    if (value.isBefore(startDate) || value.isSame(startDate, 'day')) {
                                                        return Promise.reject(new Error('Start Date must be earlier than End Date'));
                                                    }
                                                    return Promise.resolve();
                                                },
                                            }),
                                        ]}
                                    >
                                        <DatePicker style={{ width: '100%' }} placeholder="Select end date" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}

                    {modalType === 'experience' && (
                        <>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Company Name"
                                        name="company_name"
                                        rules={[{ required: true, message: 'Please enter company name!' }]}
                                    >
                                        <Input placeholder="Enter company name" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Job Title"
                                        name="job_title"
                                        rules={[{ required: true, message: 'Please enter job title!' }]}
                                    >
                                        <Input placeholder="Enter job title" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Start Date"
                                        name="start_date"
                                        rules={[{ required: true, message: 'Please select start date!' }]}
                                    >
                                        <DatePicker style={{ width: '100%' }} placeholder="Select start date" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="End Date"
                                        name="end_date"
                                        dependencies={['start_date']}
                                        rules={[
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    const startDate = getFieldValue('start_date');
                                                    if (!value || !startDate) {
                                                        return Promise.resolve();
                                                    }
                                                    if (value.isBefore(startDate) || value.isSame(startDate, 'day')) {
                                                        return Promise.reject(new Error('Start Date must be earlier than End Date'));
                                                    }
                                                    return Promise.resolve();
                                                },
                                            }),
                                        ]}
                                    >
                                        <DatePicker style={{ width: '100%' }} placeholder="Select end date" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item
                                        label="Responsibilities"
                                        name="responsibilities"
                                    >
                                        <TextArea rows={4} placeholder="Enter responsibilities" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}

                    {modalType === 'emergency' && (
                        <>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Full Name"
                                        name="name"
                                        rules={[{ required: true, message: 'Please enter full name!' }]}
                                    >
                                        <Input placeholder="Enter full name" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Relationship"
                                        name="relationship"
                                        rules={[{ required: true, message: 'Please select relationship!' }]}
                                    >
                                        <Select placeholder="Select relationship">
                                            {['Father', 'Mother', 'Spouse', 'Sibling', 'Child', 'Friend', 'Other'].map(option => (
                                                <Option key={option} value={option}>
                                                    {option}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Phone Number"
                                        name="phone_number"
                                        rules={[
                                            { required: true, message: 'Please enter phone number!' },
                                            { pattern: /^[0-9+\-\s()]{10,15}$/, message: 'Invalid phone number!' }
                                        ]}
                                    >
                                        <Input placeholder="Enter phone number" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Alternate Phone"
                                        name="alternate_phone"
                                        rules={[
                                            { pattern: /^[0-9+\-\s()]{10,15}$/, message: 'Invalid phone number!' }
                                        ]}
                                    >
                                        <Input placeholder="Enter alternate phone" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Email"
                                        name="email"
                                        rules={[{ type: 'email', message: 'Invalid email format!' }]}
                                    >
                                        <Input placeholder="Enter email" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Primary Contact"
                                        name="is_primary"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item
                                        label="Address"
                                        name="address"
                                    >
                                        <TextArea rows={3} placeholder="Enter address" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}

                    <div style={{ textAlign: 'right', marginTop: 16 }}>
                        <Space>
                            <Button onClick={handleModalCancel}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                {editingRecord ? 'Update' : 'Add'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            <SetPassword
                visible={showSetPassword}
                onCancel={() => setShowSetPassword(false)}
                onSuccess={() => {
                    setShowSetPassword(false);
                    message.success('Password set successfully!');
                }}
                user={selectedUser}
            />
        </div>
    );
};

export default EmployeeDetail;
